/* eslint-disable no-case-declarations */
import { useRef, useState, useCallback } from 'react'

import tw from 'twin.macro'
import { graphql } from 'gatsby'

import Link from '@/components/link-component'
import SearchNavIcon from '@/components/search/nav-icon'
import NavContainer from '@/components/nav-container'
import HeaderDropdownMenu from '@/components/header-dropdown-menu'
import HeaderRichMenu, { HeaderRichMenuContent } from '@/components/header-rich-menu'
import CtaLink, { SizeType, buttonStyles } from '@/components/ui/cta-link'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import HeaderBanner from '@/components/header-banner'
import Disclosure from '@/components/ui/disclosure'
import DynamicAction from '@/components/ui/dynamic-action'
import { ExperienceMapper } from '@ninetailed/experience.js-utils'
import { Experience } from '@ninetailed/experience.js-gatsby'

import routes from '@/constants/routes'

import LogoHorizontal from '@/icons/logo-horizontal.svg'
import ArrowDown from '@/icons/bwi/angle-down.svg'

import { useHasScrolled } from '@/utils/dom'
import colors from '@/constants/colors'
import { AnyLinkType, flattenLinkGroups, useDefaultHeader } from '@/utils/header'

export const fragment = graphql`
  fragment HeaderLinkGroup on ContentfulHeaderLinkGroup {
    __typename
    contentful_id
    links {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulHeaderDropdownMenu {
        ...HeaderDropdownMenu
      }
      ... on ContentfulHeaderRichMenu {
        ...HeaderRichMenu
      }
    }
  }

  fragment Header on ContentfulHeader {
    __typename
    contentful_id
    node_locale
    mainNavItems {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulHeaderDropdownMenu {
        ...HeaderDropdownMenu
      }
      ... on ContentfulHeaderRichMenu {
        ...HeaderRichMenu
      }
      ... on ContentfulHeaderLinkGroup {
        ...HeaderLinkGroup
      }
    }
    ctaNavItems {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulHeaderLinkGroup {
        ...HeaderLinkGroup
      }
    }
    isDefault
    showSearch
    backgroundColor
  }
`

function MobileDisclosureArrow(isOpen) {
  return <ArrowDown css={[tw`w-[28px] h-[28px] text-indigoBlue transition-transform`, isOpen && tw`rotate-180`]} />
}

export default function Header(props): JSX.Element {
  const Loader = useCallback(
    () => (
      <div tw="bg-primaryBlue opacity-95 sticky top-0 left-0 z-50 ">
        <div tw="animate-pulse bg-primaryBlue w-full h-[72px] lg:h-[64px] xl:h-[68px] relative hidden max-w-8xl mx-auto px-9 justify-between gap-4 lg:flex">
          <div tw="flex items-center">
            <div tw="w-36 2xl:w-44 py-3 mr-6 xl:mr-10 2xl:mr-12">
              <LogoHorizontal tw="text-white" />
            </div>
          </div>
        </div>
      </div>
    ),
    []
  )

  const mapExperiences = useCallback((experiences) => {
    if (!experiences || experiences.length === 0 || !experiences[0]?.variants) return []
    return experiences
      .map(({ variants, ...rest }) => ({
        ...rest,
        variants: variants.map((v) => ({ headerNav: { ...v }, id: v.contentful_id })),
      }))
      .filter(ExperienceMapper.isExperienceEntry)
      .map(ExperienceMapper.mapExperience)
  }, [])

  return (
    <Experience
      {...props}
      id={props.headerNav?.contentful_id}
      key={props.headerNav?.contentful_id}
      experiences={mapExperiences(props.headerNav?.nt_experiences)}
      component={HeaderComponent}
      loadingComponent={Loader}
      passthroughProps={{ ...props }}
    />
  )
}

export function HeaderComponent({ headerNav }: { headerNav: Queries.HeaderFragment | null }) {
  const defaultHeader = useDefaultHeader()
  const header: Queries.HeaderFragment | null = headerNav || defaultHeader
  const { backgroundColor = colors.primaryBlue } = header
  const mainNavLinks = flattenLinkGroups(header.mainNavItems)
  const ctaNavLinks = flattenLinkGroups(header.ctaNavItems)

  const updateMobileDownloadLink = (ctaLink: Queries.CtaLinkFragment) => {
    if (ctaLink?.link?.url?.endsWith('/download/')) {
      return {
        ...ctaLink,
        link: {
          ...ctaLink,
          url: `${ctaLink.link.url}#downloads-mobile`,
        },
      }
    }
    return ctaLink
  }
  const transformCustomMobileLinks = (link: AnyLinkType) => {
    switch (link.__typename) {
      case 'ContentfulComponentCtaLink':
        return updateMobileDownloadLink(link)
      case 'ContentfulHeaderDropdownMenu':
        return {
          ...link,
          links: link.links.map((nestedLink) => updateMobileDownloadLink(nestedLink)),
        }
      case 'ContentfulHeaderRichMenu':
        return {
          ...link,
          cards: link.cards.map((card) => ({
            ...card,
            links: card.links.map((nestedLink) => ({
              ...nestedLink,
              link: updateMobileDownloadLink(nestedLink.link),
            })),
          })),
          links: link.links,
        }
      default:
        break
    }
    return link
  }
  const AreMainNavItemsEmpty = (header.mainNavItems?.length || 0) === 0
  const mobileMainNavLinks = flattenLinkGroups(!AreMainNavItemsEmpty ? header.mainNavItems : header.ctaNavItems).map(
    transformCustomMobileLinks
  )

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const closeDrawer = () => setIsDrawerOpen(false)
  const [topRef, hasScrolled] = useHasScrolled()
  const headerRef = useRef()

  let ctaBtnSolidCount = 0
  let ctaBtnSolidCountMobile = 0
  return (
    <>
      <div ref={topRef as never} tw="h-0" />
      <HeaderBanner />
      <header
        css={[
          tw`sticky top-0 left-0 z-50 transition duration-300`,
          hasScrolled && tw`shadow-lg`,
          backgroundColor === colors.primaryBlue && tw`bg-primaryBlue text-white dark:bg-gray-800`,
          backgroundColor === colors.white && tw`bg-white text-primaryBlue dark:(bg-gray-800 text-white)`,
          isDrawerOpen && tw`z-[10000]`,
        ]}
        ref={headerRef}
      >
        {/* Mobile Nav */}
        <nav
          data-testid="bw-mobile-header"
          tw="relative max-w-7xl mx-auto px-5 flex justify-between items-center py-3 lg:hidden"
        >
          <div tw="w-40">
            <Link to={routes.index} title="Bitwarden homepage">
              <LogoHorizontal
                css={[
                  backgroundColor === colors.primaryBlue && tw`text-white`,
                  backgroundColor === colors.white && tw`text-primaryBlue dark:text-white`,
                ]}
              />
            </Link>
          </div>
          <div tw="flex gap-3 items-center">
            {header.showSearch && <SearchNavIcon />}
            {mobileMainNavLinks.length + ctaNavLinks.length > 0 && (
              <div tw="flex justify-center w-7">
                <button
                  type="button"
                  tw="font-bwi cursor-pointer transition-colors duration-200 text-[2rem] hover:text-indigoBlue dark:text-white"
                  css={isDrawerOpen && tw`pointer-events-none`}
                  data-name="bwi-hamburger"
                  onClick={() => {
                    setIsDrawerOpen(!isDrawerOpen)
                  }}
                >
                  &#xe972;
                </button>
              </div>
            )}
          </div>

          {/* Mobile Dropdown */}
          <NavContainer
            position="right"
            isDrawerOpen={isDrawerOpen}
            drawerBreakpoint="lg"
            tw="bg-gray-100 pb-16"
            onRequestClose={closeDrawer}
            headerRef={headerRef}
            showOverlay={false}
            fullWidth
          >
            <div tw="flex flex-col min-h-[calc(100vh - var(--nav-container-drawer-offset))]">
              <StaggerList tw="flex-1 list-none pb-[var(--nav-container-drawer-offset)]" isVisible={isDrawerOpen}>
                {mobileMainNavLinks.map((link) => {
                  if (!link) return null
                  switch (link.__typename) {
                    case 'ContentfulComponentCtaLink':
                      return (
                        <StaggerListItem key={link.id}>
                          <DynamicAction
                            tw="no-underline bg-transparent! block p-6 w-full duration-200 text-xl text-indigoBlue! hover:text-indigoBlue border-0 border-b-[1px] border-gray-300 rounded-none"
                            onClick={closeDrawer}
                            scrollToOffset={200}
                            {...(link as any)}
                          />
                        </StaggerListItem>
                      )
                    case 'ContentfulHeaderDropdownMenu':
                      return (
                        <StaggerListItem
                          key={link.id}
                          css={[
                            tw`p-0 border-b-[1px] border-b-gray-300`,
                            tw`[>button]:(p-6 w-full rounded-none bg-gray-100! items-center justify-between)`,
                          ]}
                        >
                          <Disclosure
                            heading={
                              <div tw="flex items-center justify-between text-xl text-indigoBlue">{link.title}</div>
                            }
                            ArrowIcon={MobileDisclosureArrow}
                          >
                            <ul tw="flex flex-col">
                              {link.links.map((nestedLink) => (
                                <li tw="list-none" key={nestedLink.id}>
                                  <DynamicAction
                                    tw="no-underline block bg-transparent py-6 pl-10 font-normal text-xl text-indigoBlue! border-t-[1px] border-gray-200"
                                    onClick={closeDrawer}
                                    {...(nestedLink as any)}
                                  />
                                </li>
                              ))}
                            </ul>
                          </Disclosure>
                        </StaggerListItem>
                      )
                    case 'ContentfulHeaderRichMenu':
                      return (
                        <StaggerListItem
                          key={link.id}
                          css={[
                            tw`p-0 border-b-[1px] border-b-gray-300 [>button]:(p-6 w-full rounded-none bg-gray-100! items-center justify-between)`,
                          ]}
                        >
                          <Disclosure
                            heading={
                              <div tw="flex items-center justify-between text-xl text-indigoBlue">{link.title}</div>
                            }
                            ArrowIcon={MobileDisclosureArrow}
                          >
                            <div tw="px-6 pb-6">
                              <HeaderRichMenuContent
                                links={link.links}
                                cards={link.cards}
                                isCondensed
                                onLinkClick={closeDrawer}
                              />
                            </div>
                          </Disclosure>
                        </StaggerListItem>
                      )
                    default:
                      return null
                  }
                })}
              </StaggerList>
              {ctaNavLinks?.length > 0 && !AreMainNavItemsEmpty && (
                <div tw="sticky bottom-[var(--nav-container-drawer-offset)] translate-y-[62px] left-0 w-full bg-white flex flex-row flex-wrap items-center justify-between p-6">
                  {ctaNavLinks.map((ctaLink: any) => {
                    if (ctaLink?.variant === buttonStyles.btnSolid) {
                      ctaBtnSolidCountMobile += 1
                    }
                    const isAltColor = ctaBtnSolidCountMobile % 2 === 0
                    return (
                      <DynamicAction
                        key={ctaLink.id}
                        tw="no-underline text-base py-0!"
                        css={[
                          ctaLink?.variant === buttonStyles.btnSolid && tw`text-white! h-auto px-4 py-2!`,
                          ctaLink?.variant === buttonStyles.btnOutlined && tw`text-primaryBlue! h-auto px-4 py-2!`,
                          ctaLink?.variant === buttonStyles.btnPlain && tw`text-primaryBlue! p-1!`,
                          ctaLink?.variant === buttonStyles.btnArrow && tw`text-primaryBlue! p-1!`,
                          ctaLink?.variant === buttonStyles.btnSolid &&
                            (!isAltColor
                              ? tw`bg-indigoBlue! border-indigoBlue`
                              : backgroundColor === colors.primaryBlue
                                ? tw`bg-white! text-indigoBlue! border-indigoBlue`
                                : tw`bg-primaryBlue!`),
                        ]}
                        onClick={closeDrawer}
                        scrollToOffset={200}
                        {...ctaLink}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          </NavContainer>
        </nav>

        {/* Desktop Nav */}
        <nav
          data-testid="bw-desktop-header"
          tw="relative hidden max-w-8xl mx-auto py-2 px-9 justify-between gap-4 lg:flex"
        >
          <div tw="flex items-center">
            <div tw="w-36 2xl:w-44 py-3 mr-6 xl:mr-10 2xl:mr-12">
              <Link to={routes.index}>
                <LogoHorizontal
                  css={[
                    backgroundColor === colors.primaryBlue && tw`text-white`,
                    backgroundColor === colors.white && tw`text-primaryBlue dark:text-white`,
                  ]}
                  title="Bitwarden homepage"
                />
              </Link>
            </div>
            <div tw="mr-2 xl:mr-5">
              {mainNavLinks?.length > 0 && (
                <ul tw="flex gap-4 xl:gap-6 items-center list-none text-base xl:text-lg">
                  {mainNavLinks.map((link, i) => {
                    switch (link.__typename) {
                      case 'ContentfulComponentCtaLink':
                        return (
                          <li key={link.id}>
                            <DynamicAction
                              css={[
                                tw`text-base xl:text-lg no-underline bg-transparent! border-0 p-0 transition-colors duration-200`,
                                backgroundColor === colors.primaryBlue &&
                                  tw`text-white hover:text-gray-300 dark:hover:text-tealBlue`,
                                backgroundColor === colors.white &&
                                  tw`text-primaryBlue hover:text-indigoBlue dark:(text-white hover:text-tealBlue)`,
                              ]}
                              scrollToOffset={200}
                              {...(link as any)}
                              variant={buttonStyles.btnPlain}
                            />
                          </li>
                        )
                      case 'ContentfulHeaderDropdownMenu':
                        return (
                          <li key={link.id}>
                            <HeaderDropdownMenu {...link} backgroundColor={backgroundColor} />
                          </li>
                        )
                      case 'ContentfulHeaderRichMenu':
                        return (
                          <li key={link.id}>
                            <HeaderRichMenu key={i} {...link} backgroundColor={backgroundColor} />
                          </li>
                        )
                      default:
                        return null
                    }
                  })}
                </ul>
              )}
            </div>
            {header.showSearch && (
              <SearchNavIcon cssStyle={[backgroundColor === colors.white && tw`hover:text-indigoBlue`]} />
            )}
          </div>
          <div tw="flex gap-5 items-center">
            {ctaNavLinks.map((ctaLink) => {
              switch (ctaLink.__typename) {
                case 'ContentfulComponentCtaLink':
                  const isBtnSolid = ctaLink.variant === buttonStyles.btnSolid
                  const isBtnOutlined = ctaLink.variant === buttonStyles.btnOutlined
                  const isBtn = isBtnSolid || isBtnOutlined
                  if (isBtnSolid) {
                    ctaBtnSolidCount += 1
                  }
                  // Alternate button color for every other solid button
                  const isAltColor = ctaBtnSolidCount % 2 === 0
                  return (
                    <CtaLink
                      key={ctaLink.id}
                      {...ctaLink}
                      to={ctaLink.link.url}
                      size={ctaLink.size as SizeType}
                      scrollToOffset={200}
                      css={[
                        tw`no-underline text-base text-center xl:text-lg leading-5!`,
                        tw`focus:(rounded-sm outline outline-1 outline-white)`,
                        !isBtn && tw`focus:outline-offset-4`,
                        backgroundColor === colors.primaryBlue && tw`text-white hover:text-gray-300`,
                        backgroundColor === colors.white && tw`text-primaryBlue hover:text-indigoBlue`,
                        tw`dark:(text-white hover:text-tealBlue)`,
                        // General Button style
                        // - Mobile to XL: Display as Link
                        // - XL and above: Display as Button
                        ...(isBtn
                          ? [
                              tw`bg-transparent! border-0 p-0! hover:(bg-transparent!)`,
                              tw`xl:(px-6! py-3! border-2 focus:rounded-3xl)`,
                              tw`dark:(text-white hover:text-tealBlue!)`,
                            ]
                          : []),
                        // Button Outlined style
                        isBtnOutlined &&
                          backgroundColor === colors.primaryBlue &&
                          tw`xl:(border-white! text-white hover:(bg-white! text-indigoBlue!))`,
                        isBtnOutlined &&
                          backgroundColor === colors.white &&
                          tw`xl:(border-indigoBlue! text-indigoBlue hover:(bg-indigoBlue! text-white!))`,
                        // Button Solid style with primaryBlue background
                        isBtnSolid &&
                          !isAltColor &&
                          backgroundColor === colors.primaryBlue &&
                          tw`xl:(bg-indigoBlue! border-indigoBlue! hover:(bg-transparent! border-white! text-white))`,
                        // Button Solid style with white background
                        isBtnSolid &&
                          !isAltColor &&
                          backgroundColor === colors.white &&
                          tw`xl:(bg-primaryBlue! text-white! border-primaryBlue! hover:(bg-transparent! border-primaryBlue! text-primaryBlue!))`,
                        // Alternative Button Solid style with primaryBlue background
                        isBtnSolid &&
                          isAltColor &&
                          backgroundColor === colors.primaryBlue &&
                          tw`xl:(bg-white! border-white text-indigoBlue hover:(bg-transparent! border-white text-white) focus:(text-indigoBlue border-white))`,
                        // Alternative Button Solid style with white background
                        isBtnSolid &&
                          isAltColor &&
                          backgroundColor === colors.white &&
                          tw`xl:(bg-indigoBlue! border-indigoBlue! text-white! hover:(bg-transparent! border-indigoBlue! text-indigoBlue!) focus:text-indigoBlue)`,
                        // Dark Mode Button Solid style
                        isBtnSolid &&
                          !isAltColor &&
                          tw`dark:xl:(bg-transparent! border-white hover:(bg-white! text-gray-800! border-white))`,
                        isBtnSolid &&
                          isAltColor &&
                          tw`dark:xl:(bg-white! border-white! text-indigoBlue! hover:(bg-transparent! border-white! text-white!))`,
                      ]}
                    >
                      {ctaLink.label}
                    </CtaLink>
                  )
                default:
                  return null
              }
            })}
          </div>
        </nav>
      </header>
    </>
  )
}
